/**
 * Badge specs for independent reviewer and machine verdicts.
 * `matched` has no finding badge; WD low confidence comes from target metadata.
 * `no_match` uses a service-specific label: TM missing value, WD missing item.
 */

import {
  IconAlertTriangle,
  IconCheck,
  IconCircle,
  IconCircleX,
  IconExclamationCircle,
  IconPencil,
  IconDatabaseOff,
  IconSearchOff,
  IconTargetArrow,
} from "@tabler/icons-react";
import { MappingStatus, ReviewStatus } from "@models/job";

/**
 * The palette a status badge may use.
 *
 * A closed set, not a free string: a colour carries meaning here (red is "this
 * row is a problem"), so inventing one per call site is how two statuses end
 * up the same shade and a third ends up a colour that means nothing.
 */
export const StatusColor = {
  Neutral: "gray.5",
  Good: "teal.6",
  Bad: "red.6",
  Failure: "red.7",
  Edited: "blue.6",
  Warning: "orange.6",
  Machine: "yellow.7",
} as const;
export type StatusColor = (typeof StatusColor)[keyof typeof StatusColor];

export interface StatusBadgeSpec {
  /**
   * The glyph inside the badge, left of the text.
   *
   * An accelerator, never the carrier. These WERE the whole status, and that
   * was the problem: a picture of a status has to be decoded before it can be
   * read. Beside a word the roles invert — the word teaches the meaning once,
   * the shape finds it again on the five-hundredth row. It also makes the badge
   * legible without colour, which matters because colour is otherwise the only
   * thing separating a warning from a failure.
   */
  Icon: typeof IconCheck;
  color: StatusColor;
  /** i18n key for the tooltip + aria-label — the FULL explanation. */
  labelKey: string;
  /** Shown when the translation is missing. */
  fallback: string;
  /** i18n key for the badge's own text. */
  badgeKey: string;
  /**
   * The badge text, when the translation is missing.
   *
   * Deliberately short — two words at most. It sits in a table column beside
   * another badge, and a label that wraps is a label that stops being scannable.
   * Everything that does not fit lives in `fallback`, which is the tooltip.
   */
  badge: string;
}

/** What a REVIEWER did. Every row has one of these. */
export const REVIEW_STATUS_BADGE: Readonly<
  Record<ReviewStatus, StatusBadgeSpec>
> = {
  [ReviewStatus.Pending]: {
    Icon: IconCircle,
    color: StatusColor.Neutral,
    labelKey: "resultRow.status.pending",
    fallback: "Pending",
    badgeKey: "resultRow.statusBadge.pending",
    badge: "Pending",
  },
  [ReviewStatus.Accepted]: {
    Icon: IconCheck,
    color: StatusColor.Good,
    labelKey: "resultRow.status.accepted",
    fallback: "Accepted",
    badgeKey: "resultRow.statusBadge.accepted",
    badge: "Accepted",
  },
  [ReviewStatus.Rejected]: {
    Icon: IconCircleX,
    color: StatusColor.Bad,
    labelKey: "resultRow.status.rejected",
    fallback: "Rejected",
    badgeKey: "resultRow.statusBadge.rejected",
    badge: "Rejected",
  },
  [ReviewStatus.Corrected]: {
    Icon: IconPencil,
    color: StatusColor.Edited,
    labelKey: "resultRow.status.corrected",
    fallback: "Corrected",
    badgeKey: "resultRow.statusBadge.corrected",
    badge: "Corrected",
  },
  // Display-only: the client folds `mapping_status=failed` into this axis so
  // the Status cell reads one field. The API never sends it here.
  [ReviewStatus.Failed]: {
    Icon: IconExclamationCircle,
    color: StatusColor.Failure,
    labelKey: "resultRow.status.failed",
    fallback: "Failed",
    badgeKey: "resultRow.statusBadge.failed",
    badge: "Failed",
  },
};

/**
 * What the PIPELINE concluded. `null` means "no finding" — the row is fine and
 * the cell shows only the reviewer icon.
 *
 * `failed` is `null` here on purpose: it is already the reviewer icon (folded
 * in above), and two icons for one fact would read as two problems.
 */
export const NO_MATCH_TM_BADGE: StatusBadgeSpec = {
  Icon: IconSearchOff,
  color: StatusColor.Warning,
  labelKey: "jobResultsPage.status.noValueFound",
  fallback: "The field is right, but this exact value is not catalogued",
  badgeKey: "jobResultsPage.statusBadge.noValueFound",
  badge: "Not found",
};

const CATALOGUE_NOT_CHECKED_TM_BADGE: StatusBadgeSpec = {
  Icon: IconDatabaseOff,
  color: StatusColor.Warning,
  labelKey: "jobResultsPage.status.catalogueNotChecked",
  fallback: "The catalogue could not be checked for this field; this value is not confirmed missing",
  badgeKey: "jobResultsPage.statusBadge.catalogueNotChecked",
  badge: "Not checked",
};

/** A catalogue-unavailable row must not be described as a missing value. */
export const tmNoMatchBadge = (reason: string | null): StatusBadgeSpec =>
  reason === "no_target" ? CATALOGUE_NOT_CHECKED_TM_BADGE : NO_MATCH_TM_BADGE;

export const LOW_CONFIDENCE_BADGE: StatusBadgeSpec = {
  Icon: IconAlertTriangle,
  color: StatusColor.Warning,
  labelKey: "jobResultsPage.status.needsReview",
  fallback: "Low confidence — please double check",
  badgeKey: "jobResultsPage.statusBadge.needsReview",
  badge: "Require Review",
};

export const MAPPING_STATUS_BADGE: Readonly<
  Record<MappingStatus, StatusBadgeSpec | null>
> = {
  [MappingStatus.Failed]: null,
  [MappingStatus.NoMatch]: {
    Icon: IconDatabaseOff,
    color: StatusColor.Warning,
    labelKey: "jobResultsPage.status.noItemFound",
    fallback: "The catalogue has no entry for this part — a reviewer cannot fix this by picking, it needs a new entry",
    badgeKey: "jobResultsPage.statusBadge.noItemFound",
    badge: "Not in catalogue",
  },
  [MappingStatus.AutoAccepted]: {
    Icon: IconTargetArrow,
    color: StatusColor.Good,
    labelKey: "jobResultsPage.status.autoAccepted",
    fallback: "Identified outright, not ranked — the catalogue holds this exact value for this field (or a confirmed past mapping was replayed). No reviewer had to confirm it.",
    badgeKey: "jobResultsPage.statusBadge.autoAccepted",
    badge: "Exact Match",
  },
  [MappingStatus.Matched]: null,
};
